const { formatMoney, getElements } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStock: '[data-product-back-in-stock]',
  quantity: '[data-product-quantity]',
  stockMessaging: '[data-product-stock-messaging]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  priceCompare: '[data-product-price-compare]',
  priceCompareText: '[data-product-price-compare-text]',
  sku: '[data-product-sku]',
}

class Variant {
  constructor (data, element) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
    /**
     * @type {VariantOptions}
     * @public
     */
    this.element = element
  }

  render () {
    this.setAvailability()
    this.setPrice()
    this.updateInput()
  }

  setPrice () {
    unhidePriceEls()

    const priceElements = getElements(selectors.price)
    priceElements.forEach(el => {
      el.innerHTML = formatMoney(this.data.price)
      el.classList.toggle('prd-Detail_Price-sale', !!this.data.compare_at_price)
    })

    const priceCompareElements = getElements(selectors.priceCompare)
    priceCompareElements.forEach(
      el => (el.innerHTML = formatMoney(this.data.compare_at_price)),
    )

    if (!this.data.compare_at_price) {
      hidePriceCompareElements()
    }
  }

  setAvailability () {
    getElements(selectors.addToCart).forEach(el => {
      el.setAttribute('aria-hidden', !this.data.available)
      el.disabled = !this.data.available
    })

    getElements(selectors.quantity).forEach(el => {
      el.setAttribute('aria-hidden', !this.data.available)
      el.disabled = !this.data.available
    })

    getElements(selectors.backInStock).forEach(el => {
      el.setAttribute('aria-hidden', this.data.available)
      el.disabled = !this.data.available
      el.dataset.variantId = this.data.id
    })

    getElements(selectors.stockMessaging).forEach(
      el =>
        (el.textContent = this.data.available
          ? window.theme.strings.inStock
          : window.theme.strings.outOfStock),
    )

    getElements(selectors.addToCartText).forEach(
      el =>
        (el.textContent = this.data.available
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut),
    )
  }

  updateInput () {
    const inputs = getElements(`form[action*="/cart/add"] [name="id"]`)

    inputs.forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  /**
   * @param {string} name
   * @param {string} value
   *
   * @returns {void}
   */
  updateIndicator (name, value) {
    if (!this.element.els['variant-indicator']?.exists) return

    const indicator = this.element.els['variant-indicator'].elements.find(
      el => el.dataset.name === name,
    )
    if (!indicator) return

    indicator.textContent = value
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const params = new URLSearchParams(window.location.search)
    params.set('variant', this.id.toString())

    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.search = params.toString()

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant, this)]
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)
  }

  onVariantChange (e) {
    const variant = this.getVariant()

    variant.render()
    variant.updateIndicator(e.target.name, e.target.value)

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    variant.setUrl()
    variant.setShareUrl()
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data, this)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      variant => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find(variant => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find(el => el.checked)?.value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}

function hideElements (elements) {
  elements.forEach(el => (el.hidden = true))
}

function unhideElements (elements) {
  elements.forEach(el => (el.hidden = false))
}

function unhidePriceEls () {
  unhideElements(getElements(`${selectors.price}, ${selectors.priceCompare}`))
}

function hidePriceCompareElements () {
  hideElements(getElements(selectors.priceCompare))
}
